import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "submit", "progress", "progressText", "debug", "error", "removeCheckbox"]

  initialize() {
    // Controller initialized
  }

  connect() {
    if (!this.hasInputTarget) console.error("Missing input target!")
    if (!this.hasProgressTarget) console.error("Missing progress target!")
    if (!this.hasDebugTarget) console.error("Missing debug target!")

    this.debug("DirectUpload controller connected")

    // Find the submit button - either as a target or in the parent form
    this.submitButton = this.findSubmitButton()

    // Hide progress bar and text initially
    this.hideProgress()

    // Track upload state for this field
    this.isUploading = false
  }

  // Rails `ActiveStorage.start()` listens for `data-direct-upload-url`. When our
  // Stimulus controller also runs `DirectUpload` on `change`, both append signed
  // ids → duplicate attachments. Use `data-stimulus-direct-upload-url` on inputs
  // that must only be handled by this controller (e.g. has_many_attached).
  directUploadEndpoint(input) {
    return input.dataset.stimulusDirectUploadUrl || input.dataset.directUploadUrl
  }

  findSubmitButton() {
    // First try to find submit button as a target (form-level controller)
    if (this.hasSubmitTarget) {
      return this.submitTarget
    }

    // Otherwise look for submit button in the parent form (field-level controller)
    const form = this.element.closest('form')
    if (form) {
      return form.querySelector('input[type="submit"], button[type="submit"]')
    }
    return null
  }

  uploadStarted(event) {
    // If this is a change event, initialize the direct upload
    if (event.type === 'change') {
      const input = event.target
      const files = Array.from(input.files || [])

      if (files.length === 0) {
        this.isUploading = false
        this.enableSubmitButton()
        this.hideProgress()
        return
      }

      this.clearError()
      this.uncheckRemove()

      // Only use sequential multi-upload when the input allows multiple files
      // AND the user picked more than one. All existing single-file fields
      // (no `multiple`) keep the original one-file DirectUpload path unchanged.
      if (input.multiple && files.length > 1) {
        this.debug(`${files.length} files selected — uploading sequentially…`)
        this.uploadMultipleFiles(input, files)
        return
      }

      const file = files[0]

      if (file) {
        this.debug(`File selected: ${file.name} (${this.formatBytes(file.size)})`)

        // Reset UI state
        this.isUploading = true
        this.disableSubmitButton()
        this.showProgress()
        this.updateProgress(0)

        // Create the DirectUpload instance
        const url = this.directUploadEndpoint(input)
        const upload = new DirectUpload(file, url, this)

        this.debug("Starting direct upload...")
        upload.create((error, blob) => {
          if (error) {
            this.debug(`Upload failed: ${error}`, 'error')
            this.showError(error)
            this.isUploading = false
            this.enableSubmitButton()
          } else {
            this.debug(`Upload successful! Blob signed id: ${blob.signed_id}`)
            // Set the hidden field value
            const hiddenField = document.createElement('input')
            hiddenField.type = 'hidden'
            hiddenField.name = input.name
            hiddenField.value = blob.signed_id
            input.closest('form').appendChild(hiddenField)

            // Critical: clear the file input so the browser does not ALSO submit the
            // raw file on multipart forms. Otherwise Rails attaches twice: once from
            // signed_id (hidden) and once from the file field → duplicate blobs.
            input.value = ""

            // Update UI for success
            this.updateProgress(100)
            this.showSuccess()
            this.isUploading = false
            this.enableSubmitButton()
          }
        })
      } else {
        // No file selected
        this.isUploading = false
        this.enableSubmitButton()
        this.hideProgress()
      }
    } else {
      // This is the direct-upload:start event
      this.isUploading = true
      this.disableSubmitButton()
      this.showProgress()
      this.updateProgress(0)

      const file = event.detail.file
      this.debug(`Upload started for file: ${file.name} (${this.formatBytes(file.size)})`)
    }
  }

  appendSignedBlobHidden(input, blob) {
    const form = input.closest('form')
    if (!form) return

    const hiddenField = document.createElement('input')
    hiddenField.type = 'hidden'
    hiddenField.name = input.name
    hiddenField.value = blob.signed_id
    form.appendChild(hiddenField)
  }

  resetProgressBarStyle() {
    const progressBar = this.progressTarget.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.classList.remove('bg-danger', 'bg-success')
      progressBar.classList.add('bg-primary')
    }
  }

  resetProgressText() {
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.innerHTML =
        '<span class="progress-percentage">0%</span> uploaded'
      this.progressTextTarget.style.display = 'none'
    }
  }

  uploadMultipleFiles(input, files) {
    const form = input.closest('form')
    const url = this.directUploadEndpoint(input)
    const total = files.length

    if (!form || !url) {
      this.debug('Missing form or direct upload URL', 'error')
      return
    }

    this.resetProgressBarStyle()
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.innerHTML =
        '<span class="progress-percentage">0%</span> uploaded'
    }

    this.isUploading = true
    this.disableSubmitButton()
    this.showProgress()
    this.updateProgress(0)

    let index = 0

    const uploadNext = () => {
      if (index >= total) {
        this.debug(`All ${total} file(s) uploaded.`)
        this.isUploading = false
        this.updateProgress(100)
        this.showSuccess()
        input.value = ''
        this.enableSubmitButton()
        return
      }

      const file = files[index]
      this.debug(`Uploading file ${index + 1}/${total}: ${file.name}`)

      const delegate = {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener('progress', (event) => {
            const fileProgress =
              event.lengthComputable && event.total > 0
                ? event.loaded / event.total
                : 0
            const overall = ((index + fileProgress) / total) * 100
            this.updateProgress(Math.round(overall))
          })
        }
      }

      const upload = new DirectUpload(file, url, delegate)

      upload.create((error, blob) => {
        if (error) {
          this.debug(`Upload failed: ${error}`, 'error')
          this.showError(error)
          this.isUploading = false
          this.enableSubmitButton()
          return
        }

        this.appendSignedBlobHidden(input, blob)
        index += 1
        uploadNext()
      })
    }

    uploadNext()
  }

  uploadProgressing(event) {
    const progress = event.detail.progress
    const percentage = Math.round(progress * 100)

    this.updateProgress(percentage)
    this.debug(`Upload progress: ${percentage}%`)
  }

  uploadFinished(event) {
    this.isUploading = false
    this.enableSubmitButton()
    this.updateProgress(100)
    this.showSuccess()

    try {
      const file = event.detail.file || event.detail
      this.debug(`Upload completed successfully. Signed ID: ${file.signedId}`)
    } catch (error) {
      console.error("Error in uploadFinished:", error)
      this.debug(`Error processing upload completion: ${error.message}`, 'error')
    }
  }

  uploadFailed(event) {
    this.isUploading = false
    this.enableSubmitButton()

    const error = event.detail?.error || 'Upload failed'
    this.showError(error)
    this.debug(`Upload failed: ${error}`, 'error')
  }

  // Submit button management
  disableSubmitButton() {
    if (this.submitButton) {
      this.submitButton.disabled = true
    }
  }

  enableSubmitButton() {
    if (this.submitButton) {
      // Check if any other upload fields are still uploading
      const form = this.element.closest('form')
      if (form) {
        const otherUploadingFields = form.querySelectorAll('[data-controller*="direct-upload"]')
        let anyUploading = false

        otherUploadingFields.forEach(field => {
          if (field !== this.element) {
            const controller = this.application.getControllerForElementAndIdentifier(field, 'direct-upload')
            if (controller && controller.isUploading) {
              anyUploading = true
            }
          }
        })

        // Only enable if no other fields are uploading
        if (!anyUploading) {
          this.submitButton.disabled = false
        }
      }
    }
  }

  // Helper methods for progress UI
  showProgress() {
    this.progressTarget.style.display = 'block'
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.style.display = 'block'
    }
  }

  hideProgress() {
    this.progressTarget.style.display = 'none'
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.style.display = 'none'
    }
  }

  updateProgress(percentage) {
    const progressBar = this.progressTarget.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.style.width = `${percentage}%`
      progressBar.setAttribute('aria-valuenow', percentage)
    }

    if (this.hasProgressTextTarget) {
      const percentageSpan = this.progressTextTarget.querySelector('.progress-percentage')
      if (percentageSpan) {
        percentageSpan.textContent = `${percentage}%`
      }
    }
  }

  showSuccess() {
    this.clearError()

    const progressBar = this.progressTarget.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.classList.remove('bg-danger')
      progressBar.classList.add('bg-success')
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.innerHTML = '<span class="text-success">✓ Upload complete!</span>'
    }
  }

  showError(message = 'Upload failed') {
    const progressBar = this.progressTarget.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.classList.remove('bg-primary', 'bg-success')
      progressBar.classList.add('bg-danger')
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.style.display = 'block'
      this.progressTextTarget.innerHTML = `<span class="text-danger">✗ ${this.escapeHtml(message)}</span>`
    }

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.hidden = false
      this.errorTarget.classList.add('d-block')
    }

    if (this.hasInputTarget) {
      this.inputTarget.classList.add('is-invalid')
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ''
      this.errorTarget.hidden = true
      this.errorTarget.classList.remove('d-block')
    }

    if (this.hasInputTarget) {
      this.inputTarget.classList.remove('is-invalid')
    }
  }

  uncheckRemove() {
    if (this.hasRemoveCheckboxTarget) {
      this.removeCheckboxTarget.checked = false
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = String(text)
    return div.innerHTML
  }

  debug(message, type = 'info') {
    if (this.hasDebugTarget) {
      const timestamp = new Date().toLocaleTimeString()
      const debugLine = document.createElement('div')
      debugLine.className = `text-${type === 'error' ? 'danger' : 'muted'} small`
      debugLine.textContent = `[${timestamp}] ${message}`
      this.debugTarget.insertBefore(debugLine, this.debugTarget.firstChild)
    }
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  // DirectUpload delegate methods
  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener("progress", event => {
      const progress = event.loaded / event.total
      this.uploadProgressing({ detail: { progress } })
    })
  }
}
